import { getFormattedTimes } from "utils/timeUtils";
import styled, { css } from "styled-components/macro";
import {
  TITLE_LABEL_HEIGHT,
  TREND_CHART_BAR_WIDTH,
} from "../../TrendChartStyles";
import { TrendChartBinSizesEnum } from "types/enums";
import {
  useFindDataDumpForTrendChart,
  useFindDataDumpForTrendChartCounts,
  useFormatZoned,
  useFullRangeDataDump,
  useIsSingleProcessPage,
  useTrendChartBinSize,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";
import {
  alertColor,
  darkModeElementBorderColor,
  darkModeTitleBackgroundColor,
  goodColor,
  lightSurface3,
} from "constants/colors";
import { isEmptyObject } from "../../trendChartUtils";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useIsDarkMode } from "store/atoms";
import {
  getOutputLossSum,
  useFixedTargetsOverride,
} from "components/AheadBehind/aheadBehindUtils";
import { AHEAD_BEHIND_OUTPUT } from "constants/constants";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { calcMetric } from "utils/metrics";
import {
  DataDumpAggregateType,
  RollupWithTagsAndCountsAndRange,
} from "types/types";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useShouldUseDataDumpsForTrendChartCenterMetric } from "utils/customHooks/whichServerHooks";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { Tooltip } from "@mui/material";
import { memo, useMemo } from "react";

const MUI_BUTTON_BORDER = `1px solid rgba(0, 0, 0, 0.23)`;
const TITLE_LABEL_PADDING_TOP = 0;

type TrendChartBarTitleProps = {
  isCurrentBar: boolean;
  startTimeMs: number;
  endTimeMs: number;
  rollupMetric: RollupWithTagsAndCountsAndRange;
  dataDumpForThisBar: DataDumpAggregateType | null;
};
const TrendChartBarTitle = memo<TrendChartBarTitleProps>(
  ({
    isCurrentBar,
    startTimeMs,
    endTimeMs,
    rollupMetric,
    dataDumpForThisBar,
  }) => {
    const { trendChartBinSize } = useTrendChartBinSize();
    const [flightConfig] = useFlightConfig();
    const processGroup = useVisibleGroups()[0];
    const trafficLightTargetsPerProcess =
      flightConfig.trendChart.trafficLightsTargetsPerProcess;

    const formatZoned = useFormatZoned();
    const {
      hour: startHour,
      minute: startMinute,
      day: startDay,
      month: startMonth,
    } = getFormattedTimes({
      time: startTimeMs,
      formatZoned,
    });
    const {
      hour: endHour,
      minute: endMinute,
      day: endDay,
      month: endMonth,
    } = getFormattedTimes({
      time: endTimeMs,
      formatZoned,
    });

    const isHour = trendChartBinSize === TrendChartBinSizesEnum.hours;
    const isDay = trendChartBinSize === TrendChartBinSizesEnum.days;
    const isWeek = trendChartBinSize === TrendChartBinSizesEnum.weeks;

    const processId = useProcessIdOnSingleProcessPage();
    const isSingleProcessPage = useIsSingleProcessPage();
    const showTrafficLights =
      (processId || !isSingleProcessPage) &&
      !isEmptyObject(trafficLightTargetsPerProcess);
    const overrideCountMachemetryIds =
      flightConfig.trendChart.overrideCountMachemetryIdsPerGroup?.[
        processGroup.shortName
      ] ??
      flightConfig.trendChart.overrideCountMachemetryIdsPerGroup?.[
        processGroup.longName
      ] ??
      [];
    // flightConfig.trendChart.overrideCountMachemetryidsPerGroup only applies to the group page (so not single-process pages)
    const overrideProcessId = isSingleProcessPage
      ? null
      : (overrideCountMachemetryIds?.join(",") ?? null);

    const trafficLightTargetForProcess = processId
      ? trafficLightTargetsPerProcess[overrideProcessId ?? processId] ||
        trafficLightTargetsPerProcess.ALL
      : null;

    const shouldUseABCalc =
      (trafficLightTargetsPerProcess.ALL as unknown as string) ===
      AHEAD_BEHIND_OUTPUT;

    const shouldUseDataDumpsForTrendChartCenterMetric =
      useShouldUseDataDumpsForTrendChartCenterMetric();

    const { preferredMetric } = useSettings();
    const metricPercent = calcMetric({
      rollupMetric,
      dataDumpKey: preferredMetric,
      dataDumpsObjForRange: dataDumpForThisBar,
      isMidgardDDAEnabled: shouldUseDataDumpsForTrendChartCenterMetric,
    });

    let isTitleGreen =
      showTrafficLights &&
      trafficLightTargetForProcess !== null &&
      (metricPercent || metricPercent === 0) &&
      metricPercent >= trafficLightTargetForProcess;

    const dataDump = useFindDataDumpForTrendChart({
      geToFind: startTimeMs,
    });
    const countsDataDump = useFindDataDumpForTrendChartCounts({
      geToFind: startTimeMs,
    });

    const dataDumpFullRange = useFullRangeDataDump();
    const dataDumpForCounts = countsDataDump ?? dataDump;
    const { currentFixedTarget } = useFixedTargetsOverride(
      dataDumpForCounts,
      dataDumpFullRange
    );
    const targetScale =
      flightConfig.general.aheadBehindScaleTargetRateColorOnly[
        processId ?? "ALL"
      ] ??
      flightConfig.general.aheadBehindScaleTargetRate[processId ?? "ALL"] ??
      1;
    const outputLossSum = getOutputLossSum(
      targetScale,
      dataDumpForCounts,
      currentFixedTarget ?? undefined
    );

    if (shouldUseABCalc) {
      isTitleGreen =
        (outputLossSum || outputLossSum === 0) && outputLossSum >= 0;
    }

    const isInFuture = startTimeMs > Date.now();
    const isTitleGrey =
      isSingleProcessPage &&
      !isInFuture &&
      shouldUseABCalc &&
      outputLossSum === null;

    const isTitleRed = showTrafficLights && !isInFuture && !isTitleGreen;

    const [isDarkMode] = useIsDarkMode();
    const isStorybook = useIsStorybook();
    const storybookOverrideHour = isStorybook ? "00" : null;
    const tooltipTitle = useMemo(
      () =>
        overrideProcessId
          ? `* counts from ${
              processGroup.processes.find(p => p.id === overrideProcessId)
                ?.name ?? overrideProcessId
            }`
          : "",
      [processGroup, overrideProcessId]
    );
    return (
      <Tooltip title={tooltipTitle}>
        <BarTitleStyles
          {...{
            $isTitleGreen: Boolean(isTitleGreen),
            $isTitleGrey: Boolean(isTitleGrey),
            $isTitleRed: Boolean(isTitleRed),
            $isDarkMode: isDarkMode,
            $isInFuture: isInFuture,
          }}
          data-testid={`trendChartBarTitle-${startHour}`}
        >
          <div className="outlinedWrapper">
            {overrideProcessId ? (
              <div className="overridden-indicator">*</div>
            ) : null}
            {isHour ? (
              <div
                className={`label titleLabel${isCurrentBar ? " callout" : ""}`}
              >
                <div className="timeStart">
                  {storybookOverrideHour ?? startHour}:{startMinute}
                </div>
                <div className="timeEnd">
                  {storybookOverrideHour ?? endHour}:{endMinute}
                </div>
              </div>
            ) : isDay || isWeek ? (
              <div
                className={`label titleLabel ${isCurrentBar ? " callout" : ""}`}
              >
                <div className="dateStart">
                  {startMonth} {startDay}
                </div>
                <div className="dateEnd">
                  {endMonth} {endDay}
                </div>
              </div>
            ) : null}
          </div>
        </BarTitleStyles>
      </Tooltip>
    );
  }
);

export default TrendChartBarTitle;

export const BarTitleStyles = styled.div<{
  $isTitleGreen: boolean;
  $isTitleGrey: boolean;
  $isTitleRed: boolean;
  $isInFuture: boolean;
  $isDarkMode: boolean;
}>`
  flex-shrink: 0;
  padding-top: ${TITLE_LABEL_PADDING_TOP}px;
  height: ${TITLE_LABEL_HEIGHT - TITLE_LABEL_PADDING_TOP}px;
  .outlinedWrapper {
    border-radius: 4px;
    border: ${({ $isDarkMode }) =>
      $isDarkMode
        ? `1px solid ${darkModeElementBorderColor}`
        : MUI_BUTTON_BORDER};
    text-transform: none;
    width: fit-content;
    min-width: 0;
    box-sizing: border-box;
    height: ${TITLE_LABEL_HEIGHT}px;
    width: ${TREND_CHART_BAR_WIDTH}px;
    ${props =>
      props.$isTitleGrey
        ? props.$isDarkMode
          ? css`
              background: ${darkModeTitleBackgroundColor};
              color: white;
            `
          : css`
              background: ${lightSurface3};
            `
        : props.$isTitleRed
          ? css`
              background: ${alertColor};
              color: white;
            `
          : props.$isTitleGreen
            ? css`
                background: ${goodColor};
                color: white;
              `
            : ""}
    .titleLabel {
      line-height: 16px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      white-space: nowrap;
      width: 100%;
      height: 100%;
      ${({ $isInFuture }) =>
        $isInFuture
          ? css`
              opacity: 0.25;
            `
          : ""}
      .timeStart {
        margin-top: 1px;
      }
      .dateStart,
      .timeStart {
        margin-bottom: 2px;
      }
      .timeStart,
      .timeEnd {
        text-align: center;
        position: relative;
      }
      .weekDay,
      .minutes {
        font-size: 0.6em;
      }
    }
  }
  .callout {
    font-weight: bold;
    transform: scale(1.05);
  }
  .overridden-indicator {
    position: absolute;
    top: -3px;
    right: 12px;
  }
`;
